import {
  drop,
  flow,
  get,
  has,
  join,
  last,
  map,
  mapKeys,
  split,
  toNumber,
  dropRight,
} from 'lodash/fp';
import { stringifyQuery } from 'helpers/location';
import * as parameterTypes from './parameterTypes';

const ITEM_SEPARATOR = '||';
const FIELD_SEPARATOR = '&';
const PARAMETER_SEPARATOR = '_';
const QUEUE_LIMIT = 15;
const TIMESTAMP_FIELD_KEY = '_cl';
const COOKIE_TIME_LIMIT_IN_SECONDS = 3600; // 1 hour
const ONE_SECOND_IN_MS = 1000;

export const PARAMETER_ALIASES = {
  [parameterTypes.UTM_EXPID]: 'expid',
};

const aliasParameters = ({ params, aliases }) =>
  mapKeys((key) => (has([key], aliases) ? get([key], aliases) : key))(params);

const getCurrentTimeInSeconds = () => Math.round(Date.now() / ONE_SECOND_IN_MS);

const getCookieTimestamp = ({ parameters }) =>
  flow(split(`${TIMESTAMP_FIELD_KEY}=`), last, toNumber)(parameters);

const isLastCookieExpired = (timestamp) =>
  getCurrentTimeInSeconds() - timestamp > COOKIE_TIME_LIMIT_IN_SECONDS;

const canAppendValueToQueue = (value, queue) => {
  const lastValue = last(queue);

  if (lastValue) {
    const timestamp = getCookieTimestamp(lastValue);

    return lastValue.cid === value ? isLastCookieExpired(timestamp) : true;
  }

  return true;
};

const createObjToAdd = ({ cid, parameters }) => ({
  cid,
  parameters: `${parameters}${TIMESTAMP_FIELD_KEY}=${getCurrentTimeInSeconds()}`,
});

const queryToCookieParams = (params) =>
  stringifyQuery(params, {
    addQueryPrefix: false,
    delimiter: PARAMETER_SEPARATOR,
    format: 'RFC1738',
  });

export const appendToQueue = ({
  value: cid,
  queue,
  parameters: queryParams,
  aliases,
}) => {
  if (!canAppendValueToQueue(cid, queue)) {
    return queue;
  }

  const aliasedParameters = aliasParameters({ params: queryParams, aliases });
  const parameters = queryToCookieParams(aliasedParameters);
  const newQueue = queue.concat(createObjToAdd({ cid, parameters }));

  return newQueue.length > QUEUE_LIMIT ? drop(1, newQueue) : newQueue;
};

export const stringifyQueue = (queue = []) =>
  flow(
    map(({ cid, parameters }) => join(FIELD_SEPARATOR)([cid, parameters])),
    join(ITEM_SEPARATOR),
  )(queue);

export const initQueue = (value = '') => {
  const list = value ? value.split(ITEM_SEPARATOR) : [];

  return list.map((item) => {
    const fields = item.split(FIELD_SEPARATOR);
    const [cid, parameters] = fields;

    return { cid, parameters };
  });
};

export function injectExpId(queue = [], expid) {
  const item = last(queue);

  if (item && expid) {
    const { cid, parameters } = item;

    if (!parameters.includes('expid=') || !parameters.includes(expid)) {
      return dropRight(1, queue).concat([
        { cid, parameters: `expid=${expid}_${parameters.replace(/^_+/, '')}` },
      ]);
    }
  }

  return queue;
}
